import React, { useEffect, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { WorkspaceStatusPill } from '../../../../shared/ui/workflow/WorkflowWorkspacePrimitives';
import {
  isLearnedAutoFillMergeItemActionable,
  type LearnedAutoFillMergeItem,
  type LearnedAutoFillPatternV1,
} from '../../../../shared/lib/quickFill/learnedAutoFillLibrary';
import type { QuickFillKeyLibraryEditableField } from '../../lib/quickFillKeyLibraryDrafts';
import type { QuickFillKeyLibraryDialogSource } from '../../lib/quickFillKeyLibraryTableModel';
import QuickFillKeyLibraryEntryPreview from './QuickFillKeyLibraryEntryPreview';
import QuickFillBuiltInKeyLibraryTable, {
  PIPEEX_BUILT_IN_KEY_COUNT,
} from './QuickFillBuiltInKeyLibraryTable';
import QuickFillKeyLibrarySharingControls from './QuickFillKeyLibrarySharingControls';
import QuickFillKeyLibraryMatchTester from './QuickFillKeyLibraryMatchTester';
import type { QuickFillKeyLibraryMergeSummary } from '../../hooks/useQuickFillKeyLibrary';
import {
  getKeyLibraryContentSx,
  getKeyLibraryDialogPaperSx,
  getKeyLibraryFooterSx,
  getKeyLibraryHeaderSx,
  getKeyLibraryIconButtonSx,
  getKeyLibraryTabsSx,
} from './quickFillKeyLibraryStyles';

export type { QuickFillKeyLibraryDialogSource } from '../../lib/quickFillKeyLibraryTableModel';

export interface QuickFillKeyLibraryDialogProps {
  open: boolean;
  source: QuickFillKeyLibraryDialogSource;
  libraryEntryCount: number;
  libraryEntries: readonly LearnedAutoFillPatternV1[];
  mergePlan: LearnedAutoFillMergeItem[];
  mergeSummary: QuickFillKeyLibraryMergeSummary;
  skippedRowCount: number;
  duplicateRowCount: number;
  builtInShadowedIds: ReadonlySet<string>;
  localBuiltInShadowedIds: ReadonlySet<string>;
  confirmedConflictIds: ReadonlySet<string>;
  selectedLocalEntryIndexes: ReadonlySet<number>;
  error?: string;
  notice?: string;
  libraryValidationError?: string;
  hasUnsavedLibraryChanges: boolean;
  canAddLibraryEntry: boolean;
  guardIntent?: 'close' | 'import' | 'delete-selected';
  onClose: () => void;
  onCancelGuard: () => void;
  onDiscardGuard: () => void;
  onDraftEntryChange: (
    index: number,
    field: QuickFillKeyLibraryEditableField,
    value: string
  ) => void;
  onLibraryEntryChange: (
    index: number,
    field: QuickFillKeyLibraryEditableField,
    value: string
  ) => void;
  onDeleteLibraryEntry: (index: number) => void;
  onAddLibraryEntry: () => void;
  onRevertLibrary: () => void;
  onToggleLocalEntrySelection: (index: number, selected: boolean) => void;
  onToggleVisibleLocalEntries: (indexes: readonly number[], selected: boolean) => void;
  onRequestBulkDelete: () => void;
  onSaveLibrary: () => void;
  onConfirmConflict: (id: string, confirmed: boolean) => void;
  onConfirm: () => void;
  onExport: () => void;
  onImportFile: (file: File) => void | Promise<void>;
}

const QuickFillKeyLibraryDialog: React.FC<QuickFillKeyLibraryDialogProps> = ({
  open,
  source,
  libraryEntryCount,
  libraryEntries,
  mergePlan,
  mergeSummary,
  skippedRowCount,
  duplicateRowCount,
  builtInShadowedIds,
  localBuiltInShadowedIds,
  confirmedConflictIds,
  selectedLocalEntryIndexes,
  error,
  notice,
  libraryValidationError,
  hasUnsavedLibraryChanges,
  canAddLibraryEntry,
  guardIntent,
  onClose,
  onCancelGuard,
  onDiscardGuard,
  onDraftEntryChange,
  onLibraryEntryChange,
  onDeleteLibraryEntry,
  onAddLibraryEntry,
  onRevertLibrary,
  onToggleLocalEntrySelection,
  onToggleVisibleLocalEntries,
  onRequestBulkDelete,
  onSaveLibrary,
  onConfirmConflict,
  onConfirm,
  onExport,
  onImportFile,
}) => {
  type LibrarySection = 'builtIn' | 'local' | 'pending' | 'test';
  const [activeSection, setActiveSection] = useState<LibrarySection>('local');
  const actionCount = mergePlan.filter((item) =>
    isLearnedAutoFillMergeItemActionable(item, confirmedConflictIds)
  ).length;
  const showFooterActions = hasUnsavedLibraryChanges || (
    source !== 'manage' && actionCount > 0
  );

  useEffect(() => {
    if (open) {
      setActiveSection(source === 'manage' ? 'local' : 'pending');
    }
  }, [open, source]);

  const handleAddLocalKey = () => {
    onAddLibraryEntry();
    setActiveSection('local');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      aria-labelledby="quick-fill-key-library-title"
      PaperProps={{ sx: getKeyLibraryDialogPaperSx }}
    >
      <Box
        component="header"
        data-testid="key-library-compact-header"
        data-workspace-chrome="quick-fill"
        sx={getKeyLibraryHeaderSx}
      >
        <Typography
          component="h2"
          id="quick-fill-key-library-title"
          data-key-library-title
        >
          Auto-fill dictionary
        </Typography>
        <IconButton
          size="small"
          edge="start"
          aria-label="Back to Quick Fill"
          onClick={onClose}
          sx={(theme) => ({
            ...getKeyLibraryIconButtonSx(theme),
            gridArea: 'back',
          })}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Tabs
          value={activeSection}
          onChange={(_event, value: LibrarySection) => setActiveSection(value)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Dictionary sections"
          sx={getKeyLibraryTabsSx}
        >
          <Tab value="builtIn" label={`PipeEX rules (${PIPEEX_BUILT_IN_KEY_COUNT})`} />
          <Tab value="local" label={`Local entries (${libraryEntries.length})`} />
          {source !== 'manage' && (
            <Tab value="pending" label={`Pending (${mergePlan.length})`} />
          )}
          <Tab value="test" label="Test match" />
        </Tabs>
        <QuickFillKeyLibrarySharingControls
          libraryEntryCount={libraryEntryCount}
          editorEntryCount={libraryEntries.length}
          hasUnsavedChanges={hasUnsavedLibraryChanges}
          addLocalKeyDisabled={!canAddLibraryEntry}
          exportDisabled={hasUnsavedLibraryChanges}
          onAddLocalKey={handleAddLocalKey}
          onExport={onExport}
          onImportFile={onImportFile}
        />
      </Box>
      <DialogContent sx={getKeyLibraryContentSx}>
        <Stack spacing={0.75}>
          {error && <Alert severity="error">{error}</Alert>}
          {notice && (
            <Alert
              severity="success"
              action={(
                <Button color="inherit" size="small" onClick={onExport}>
                  Export to share
                </Button>
              )}
            >
              {notice}
            </Alert>
          )}
           {(skippedRowCount > 0 || duplicateRowCount > 0) && (
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ px: 1 }}>
              {skippedRowCount > 0 && (
                <Tooltip title="A family name, required key, or every mapping value was missing.">
                  <Box component="span" sx={{ display: 'inline-flex' }}>
                    <WorkspaceStatusPill
                      size="compact"
                      tone="warning"
                      label={`${skippedRowCount} incomplete ${skippedRowCount === 1 ? 'row' : 'rows'} skipped`}
                    />
                  </Box>
                </Tooltip>
              )}
              {duplicateRowCount > 0 && (
                <Tooltip title="Exact repeats were merged into their matching pending entry.">
                  <Box component="span" sx={{ display: 'inline-flex' }}>
                    <WorkspaceStatusPill
                      size="compact"
                      tone="info"
                      label={`${duplicateRowCount} repeated ${duplicateRowCount === 1 ? 'row' : 'rows'} consolidated`}
                    />
                  </Box>
                </Tooltip>
              )}
            </Stack>
          )}
          {source !== 'manage' && mergePlan.length > 0 && (
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ px: 1 }}>
              <WorkspaceStatusPill size="compact" tone="success" label={`${mergeSummary.newCount} new`} />
              <WorkspaceStatusPill size="compact" label={`${mergeSummary.unchangedCount} unchanged`} />
              <WorkspaceStatusPill
                size="compact"
                tone={mergeSummary.replacementRequiredCount > 0 ? 'warning' : 'neutral'}
                label={`${mergeSummary.replacementRequiredCount} replacement required`}
              />
              <WorkspaceStatusPill size="compact" tone="info" label={`${mergeSummary.actionableCount} actionable`} />
              <WorkspaceStatusPill
                size="compact"
                tone={mergeSummary.builtInShadowedCount > 0 ? 'warning' : 'neutral'}
                label={`${mergeSummary.builtInShadowedCount} built-in shadowed`}
              />
            </Stack>
          )}
          {libraryValidationError && (
            <Alert severity="error">{libraryValidationError}</Alert>
          )}
          {source !== 'manage' && hasUnsavedLibraryChanges && !libraryValidationError && (
            <Alert severity="info">
              Save the existing dictionary changes before adding pending entries.
            </Alert>
          )}
          {activeSection === 'builtIn' && <QuickFillBuiltInKeyLibraryTable />}
          {activeSection === 'local' && (
            <QuickFillKeyLibraryEntryPreview
              key={`local-${libraryEntries.length}`}
              source="manage"
              libraryEntries={libraryEntries}
              mergePlan={[]}
              builtInShadowedIds={localBuiltInShadowedIds}
              confirmedConflictIds={new Set()}
              selectedLocalEntryIndexes={selectedLocalEntryIndexes}
              onDraftEntryChange={onDraftEntryChange}
              onLibraryEntryChange={onLibraryEntryChange}
              onDeleteLibraryEntry={onDeleteLibraryEntry}
              onConfirmConflict={onConfirmConflict}
              onToggleLocalEntrySelection={onToggleLocalEntrySelection}
              onToggleVisibleLocalEntries={onToggleVisibleLocalEntries}
              onRequestBulkDelete={onRequestBulkDelete}
              onAddLibraryEntry={handleAddLocalKey}
            />
          )}
          {activeSection === 'pending' && source !== 'manage' && (
              <QuickFillKeyLibraryEntryPreview
                source={source}
                libraryEntries={[]}
                mergePlan={mergePlan}
                builtInShadowedIds={builtInShadowedIds}
                confirmedConflictIds={confirmedConflictIds}
                onDraftEntryChange={onDraftEntryChange}
                onLibraryEntryChange={onLibraryEntryChange}
                onDeleteLibraryEntry={onDeleteLibraryEntry}
                onConfirmConflict={onConfirmConflict}
              />
          )}
          {activeSection === 'test' && (
            <QuickFillKeyLibraryMatchTester entries={libraryEntries} />
          )}
        </Stack>
      </DialogContent>
      {showFooterActions && (
        <DialogActions sx={getKeyLibraryFooterSx}>
          {hasUnsavedLibraryChanges && (
            <>
              <Button size="small" variant="outlined" onClick={onRevertLibrary}>
                Revert local edits
              </Button>
              <Button
                size="small"
                variant={source === 'manage' ? 'contained' : 'outlined'}
                onClick={onSaveLibrary}
                disabled={Boolean(libraryValidationError)}
              >
                Save dictionary changes
              </Button>
            </>
          )}
          {source !== 'manage' && actionCount > 0 && (
            <Button
              size="small"
              variant="contained"
              onClick={onConfirm}
              disabled={
                actionCount === 0 || hasUnsavedLibraryChanges || Boolean(libraryValidationError)
              }
            >
              Add {actionCount} {actionCount === 1 ? 'entry' : 'entries'} to dictionary
            </Button>
          )}
        </DialogActions>
      )}
      <Dialog open={Boolean(guardIntent)} onClose={onCancelGuard} maxWidth="xs" fullWidth>
        <DialogTitle>
          {guardIntent === 'delete-selected' ? 'Delete selected local keys?' : 'Discard unsaved work?'}
        </DialogTitle>
        <DialogContent>
          {guardIntent === 'delete-selected'
            ? `${selectedLocalEntryIndexes.size} selected ${selectedLocalEntryIndexes.size === 1 ? 'entry' : 'entries'} will be removed from the edit buffer. Save dictionary changes to persist the deletion.`
            : guardIntent === 'import'
              ? 'Importing this file will replace the current edit buffer and pending preview.'
              : 'Going back now will discard local edits and the pending preview.'}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={onCancelGuard}>
            {guardIntent === 'delete-selected' ? 'Cancel' : 'Keep editing'}
          </Button>
          <Button size="small" color="error" variant="contained" onClick={onDiscardGuard}>
            {guardIntent === 'delete-selected'
              ? 'Delete selected'
              : guardIntent === 'import' ? 'Discard and import' : 'Discard and go back'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default QuickFillKeyLibraryDialog;
